import { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { FaWhatsapp } from "react-icons/fa";
import { CalendarDaysIcon, CheckBadgeIcon, PaperAirplaneIcon } from "@heroicons/react/24/outline";
import WhatsappTemplatePreviewModal from "@/app/components/ui/WhatsappTemplatePreviewModal";
import { useAppSelector } from "@/app_redux/hooks/root_hook";
import { IClickLogBody, useClickLogAPI } from "@/app/hooks/api_hooks/org/useClickLogAPI";
import {
  IAhsBookingLinkParams,
  useAhsBookingLinkScriptAPI,
} from "@/app/hooks/api_hooks/ahsBookingLink/useAhsBookingLinkScriptAPI";
import { useSendAhsBookingLinkWhatsappAPI } from "@/app/hooks/api_hooks/ahsBookingLink/useSendAhsBookingLinkWhatsappAPI";
import BookingLinkSentModal, { IBookingLinkSentResult } from "./BookingLinkSentModal";

const JOURNEY_STEPS = [
  { id: "send", title: "You send the link", detail: "Delivered on WhatsApp and SMS", Icon: PaperAirplaneIcon },
  { id: "pick", title: "Pet parent picks a slot", detail: "Live At Home availability in the Vetic app", Icon: CalendarDaysIcon },
  { id: "book", title: "Booking confirmed", detail: "Completed by the pet parent in the app", Icon: CheckBadgeIcon },
];

const AHS_BOOKING_LINK_SCREEN_ID = "AHS_BOOKING_LINK_CARD";

const AHS_BOOKING_LINK_EVENTS = {
  CARD_VIEWED: "AHS_BOOKING_LINK_CARD_VIEWED",
  SEND_LINK_CLICK: "AHS_BOOKING_LINK_SEND_LINK_CLICK",
  PREVIEW_OPENED: "AHS_BOOKING_LINK_PREVIEW_OPENED",
  PREVIEW_FAILED: "AHS_BOOKING_LINK_PREVIEW_FAILED",
  SCRIPT_COPIED: "AHS_BOOKING_LINK_SCRIPT_COPIED",
  PREVIEW_CANCELLED: "AHS_BOOKING_LINK_PREVIEW_CANCELLED",
  SEND_CONFIRM_CLICK: "AHS_BOOKING_LINK_SEND_CONFIRM_CLICK",
  SENT: "AHS_BOOKING_LINK_SENT",
  SEND_FAILED: "AHS_BOOKING_LINK_SEND_FAILED",
} as const;

const PREVIEW_STEPS = [
  "Download the Vetic App (Link shared below)",
  "Choose your preferred time slot",
  "Confirm your At home booking",
];

type AhsBookingLinkEventAction = "view" | "click" | "result";

interface IAhsBookingLinkCardProps {
  customerId?: string | null;
  petId?: string | null;
  subServiceId?: string | null;
  subServiceName?: string | null;
  customerMobile?: string;
}

const AhsBookingLinkCard: React.FC<IAhsBookingLinkCardProps> = ({
  customerId,
  petId,
  subServiceId,
  subServiceName,
  customerMobile,
}) => {
  const { execute: fetchScript } = useAhsBookingLinkScriptAPI();
  const { execute: sendLink } = useSendAhsBookingLinkWhatsappAPI();
  const { execute: logClickAPI } = useClickLogAPI();
  const vmsUserId = useAppSelector((state) => state.auth?.user?.email);

  const [isPreviewOpen, setIsPreviewOpen] = useState<boolean>(false);
  const [isSuccessOpen, setIsSuccessOpen] = useState<boolean>(false);
  const [sentResult, setSentResult] = useState<IBookingLinkSentResult | null>(null);
  const [script, setScript] = useState<string>("");
  const [mobile, setMobile] = useState<string>("");
  const [isScriptLoading, setIsScriptLoading] = useState<boolean>(false);
  const [isSending, setIsSending] = useState<boolean>(false);

  const logEvent = useCallback(
    (
      eventType: string,
      eventAction: AhsBookingLinkEventAction,
      ctaText: string | null,
      extras?: Partial<IClickLogBody>,
    ) => {
      logClickAPI({
        event_type: eventType,
        event_action: eventAction,
        scr_id: AHS_BOOKING_LINK_SCREEN_ID,
        vms_user_id: vmsUserId ?? "",
        client_id: customerId ?? undefined,
        pet_id: petId ?? undefined,
        sub_service_id: subServiceId ?? null,
        ...(ctaText ? { cta_text: ctaText } : {}),
        timestamp: Date.now(),
        ...extras,
      });
    },
    [logClickAPI, vmsUserId, customerId, petId, subServiceId],
  );

  useEffect(() => {
    logEvent(AHS_BOOKING_LINK_EVENTS.CARD_VIEWED, "view", null, { mobile: customerMobile || undefined });
  }, [customerId, petId, subServiceId]);

  const linkParams = (): IAhsBookingLinkParams => ({
    user_id: customerId ?? "",
    pet_id: petId,
    sub_service_id: subServiceId,
  });

  const handleOpenPreview = async () => {
    logEvent(AHS_BOOKING_LINK_EVENTS.SEND_LINK_CLICK, "click", "Send Booking Link", {
      mobile: customerMobile || undefined,
    });
    if (!customerId) {
      logEvent(AHS_BOOKING_LINK_EVENTS.PREVIEW_FAILED, "result", null, { failure_reason: "missing_customer_id" });
      toast.error("User information not found");
      return;
    }
    setIsScriptLoading(true);
    const { status, data } = await fetchScript(linkParams());
    setIsScriptLoading(false);
    if (!status || !data?.whatsapp_text) {
      logEvent(AHS_BOOKING_LINK_EVENTS.PREVIEW_FAILED, "result", null, { failure_reason: "script_fetch_failed" });
      toast.error("Could not load the AHS booking link");
      return;
    }
    const recipientMobile = data.phone_number || customerMobile || "";
    setScript(data.whatsapp_text);
    setMobile(recipientMobile);
    setIsPreviewOpen(true);
    logEvent(AHS_BOOKING_LINK_EVENTS.PREVIEW_OPENED, "result", null, { mobile: recipientMobile || undefined });
  };

  const handleSend = async (value: string) => {
    const mobileNumber = (value ?? "").replace(/\D/g, "");
    const recipientDetails: Partial<IClickLogBody> = {
      mobile: mobileNumber,
      original_mobile: mobile,
      mobile_edited: mobileNumber !== mobile.replace(/\D/g, ""),
    };
    logEvent(AHS_BOOKING_LINK_EVENTS.SEND_CONFIRM_CLICK, "click", "Send WhatsApp", recipientDetails);
    if (!mobileNumber) {
      logEvent(AHS_BOOKING_LINK_EVENTS.SEND_FAILED, "result", null, {
        ...recipientDetails,
        send_status: "failed",
        failure_reason: "invalid_mobile",
      });
      toast.error("Please enter a valid mobile number");
      return;
    }
    setIsSending(true);
    const result = await sendLink({ ...linkParams(), mobile: mobileNumber });
    setIsSending(false);
    logEvent(result.status ? AHS_BOOKING_LINK_EVENTS.SENT : AHS_BOOKING_LINK_EVENTS.SEND_FAILED, "result", null, {
      ...recipientDetails,
      send_status: result.status ? "success" : "failed",
      whatsapp_sent: Boolean(result.data?.whatsapp_sent),
      sms_sent: Boolean(result.data?.sms_sent),
      ...(result.status ? {} : { failure_reason: result.message }),
    });
    if (result.status) {
      setSentResult({
        mobile: mobileNumber,
        whatsappSent: Boolean(result.data?.whatsapp_sent),
        smsSent: Boolean(result.data?.sms_sent),
      });
      setIsPreviewOpen(false);
      setIsSuccessOpen(true);
    }
  };

  return (
    <div className="w-full overflow-hidden rounded-[12px] border border-[#E9ECF2] bg-white">
      <div className="flex flex-col gap-6 bg-gradient-to-br from-[#F0F6FF] via-[#F8FBFF] to-white p-5 md:flex-row md:items-stretch">
        <div className="flex-1">
          <span className="inline-flex items-center gap-1.5 rounded-full border border-[#CFE1FF] bg-white px-2.5 py-1 text-[10px] font-semibold text-[#0B6EFF]">
            <span className="h-1.5 w-1.5 rounded-full bg-[#0B6EFF]" />
            Repeat AHS customer
          </span>
          <p className="mt-3 text-[17px] font-semibold leading-6 text-[#000000]">
            Send the {subServiceName?.trim() || "At Home Service"} booking link
          </p>
          <p className="mt-1 text-[12px] text-[#6C727F]">
            This pet parent has booked At Home Service before. Send a booking link and they can{" "}
            <span className="font-semibold text-[#000000]">choose their own slot</span> in the{" "}
            <span className="font-semibold text-[#000000]">Vetic app</span>.
          </p>
          <div className="mt-5 space-y-3">
            {JOURNEY_STEPS.map(({ id, title, detail, Icon }, index) => (
              <div key={id} className="flex gap-3">
                <div className="flex flex-col items-center">
                  <div className="flex h-[32px] w-[32px] items-center justify-center rounded-full border border-[#E9ECF2] bg-white shadow-sm">
                    <Icon className="h-4 w-4 text-[#0B6EFF]" />
                  </div>
                  {index !== JOURNEY_STEPS.length - 1 && (
                    <div className="mt-1 w-px flex-1 border-l border-dashed border-[#D0D5DD]" />
                  )}
                </div>
                <div className="pb-1">
                  <p className="text-[12px] font-semibold text-[#000000]">{title}</p>
                  <p className="text-[11px] text-[#6C727F]">{detail}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="hidden w-[260px] shrink-0 md:block">
          <div className="flex h-full flex-col overflow-hidden rounded-[16px] border border-[#E9ECF2] bg-white shadow-[0_8px_24px_rgba(16,24,40,0.08)]">
            <div className="flex items-center gap-2 border-b border-[#F3F5F8] px-3 py-2.5">
              <div className="flex h-7 w-7 items-center justify-center rounded-full bg-[#000000] text-[10px] font-bold text-white">
                V
              </div>
              <div className="flex-1">
                <p className="text-[11px] font-semibold leading-4 text-[#000000]">Vetic</p>
                <p className="text-[9px] leading-3 text-[#6C727F]">Business account</p>
              </div>
              <FaWhatsapp className="h-4 w-4 text-[#25D366]" />
            </div>
            <div className="flex-1 bg-[#F3F5F8] p-3">
              <div className="rounded-[10px] rounded-tl-[2px] bg-white p-2.5 text-[10px] leading-4 text-[#344054] shadow-sm">
                <p className="font-semibold text-[#000000]">Complete Your At-Home Booking</p>
                <p className="mt-1">Hello Pet Parent,</p>
                <p className="mt-1">Thank you for calling us. Please follow the steps below:</p>
                <ul className="mt-1 space-y-0.5">
                  {PREVIEW_STEPS.map((step) => (
                    <li key={step}>🔹 {step}</li>
                  ))}
                </ul>
                <p className="mt-1">
                  Book your slot here: <span className="font-medium text-[#0B6EFF] underline">Booking link</span>
                </p>
                <p className="mt-2">
                  Regards,
                  <br />
                  Vetic
                </p>
                <p className="mt-1 text-right text-[9px] text-[#98A2B3]">now</p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="flex flex-col gap-3 border-t border-[#E9ECF2] px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-[10px] font-medium uppercase tracking-tight42 text-[#98A2B3]">Sending to</p>
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <span className="text-[13px] font-semibold text-[#000000]">
              {customerMobile || "Confirm number in the next step"}
            </span>
            <span className="inline-flex items-center gap-1 rounded-[6px] bg-[#E7F8EE] px-1.5 py-0.5 text-[10px] font-semibold text-[#128C4B]">
              <FaWhatsapp className="h-3 w-3" />
              WhatsApp
            </span>
            <span className="rounded-[6px] bg-[#F0F6FF] px-1.5 py-0.5 text-[10px] font-semibold text-[#0B6EFF]">
              SMS
            </span>
          </div>
        </div>
        <button
          type="button"
          onClick={handleOpenPreview}
          disabled={isScriptLoading}
          className="flex shrink-0 cursor-pointer items-center justify-center gap-2 rounded-[8px] bg-[#000000] px-[18px] py-[10px] text-[13px] font-semibold text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <PaperAirplaneIcon className="h-4 w-4" />
          {isScriptLoading ? "Preparing..." : "Send Booking Link"}
        </button>
      </div>
      {isPreviewOpen && (
        <WhatsappTemplatePreviewModal
          open={isPreviewOpen}
          onClose={() => {
            if (!isSending) setIsPreviewOpen(false);
          }}
          title="Send AHS Booking Link"
          initialMobile={mobile}
          script={script}
          isSending={isSending}
          onSend={handleSend}
          onCancel={() => {
            logEvent(AHS_BOOKING_LINK_EVENTS.PREVIEW_CANCELLED, "click", "Cancel", { mobile: mobile || undefined });
            setIsPreviewOpen(false);
          }}
          onCopyScript={() =>
            logEvent(AHS_BOOKING_LINK_EVENTS.SCRIPT_COPIED, "click", "Copy Script", { mobile: mobile || undefined })
          }
        />
      )}
      <BookingLinkSentModal
        open={isSuccessOpen}
        result={sentResult}
        subServiceName={subServiceName}
        onClose={() => setIsSuccessOpen(false)}
      />
    </div>
  );
};

export default AhsBookingLinkCard;
